import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderSlot as _renderSlot, unref as _unref, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createVNode as _createVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, renderList as _renderList, createSlots as _createSlots } from "vue"

const _hoisted_1 = { class: "px-4 pt-4 pb-2 flex items-start justify-between gap-4" }
const _hoisted_2 = { class: "text-lg font-medium" }
const _hoisted_3 = { class: "text-normal text-gray-500 min-h-6" }
const _hoisted_4 = { class: "flex justify-end" }
const _hoisted_5 = ["title"]
const _hoisted_6 = ["title"]

import { computed } from 'vue';
import { parseISO } from 'date-fns';
import { useMediaQuery } from '@vueuse/core';
import { ChevronRight } from 'lucide-vue-next';
import type { ColumnDef, SortingState } from '@tanstack/vue-table';
import { FacetedDataTable, type FacetOption, type FacetSelection } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import Spinner from '@/components/ui/Spinner.vue';
import ActionModal from '@/components/common/actionModal/ActionModal.vue';
import { useRecordFormModal } from '@/composables/records/useRecordFormModal';
import { locationLabel } from '@/composables/records/recordsFormat';
import type { RecordTotals } from '@/composables/records/useRecordsAggregation';
import { i18n } from '@/plugins/internationalization/i18n';
import { RECORD_ACTIONS_COLUMN_ID, type RecordTableRow } from '@/types';

/* Typographic placeholders rather than copy: a weight that was never recorded, and the marker tying
   the footer average to its note about which rows it could not include. */
const NO_VALUE = '—';
const INCOMPLETE_MARKER = '*';

/*
 * The chevron column is rendered by this component rather than through the shared cell text: it is a
 * control, not a value.
 */

/*
 * How much of the screen the rows may take before they scroll inside the table instead of pushing
 * the page down. What the cap buys is the totals row: it sits under the column names and holds
 * there while the rows move past it, so the figures stay readable at any scroll position.
 *
 * A class rather than a length: Tailwind only emits arbitrary values it can find in the source.
 */
const MAX_BODY_HEIGHT = 'max-h-[60vh]';

const LOCATION_COLUMN_MIN_WIDTH = 'min-w-48';


export default /*@__PURE__*/_defineComponent({
  __name: 'RecordsTable',
  props: {
    columns: {},
    rows: {},
    totals: { default: undefined },
    totalsSubheader: { default: () => ({}) },
    facetOptions: {},
    loading: { type: Boolean },
    heading: {},
    subtitle: {},
    sorting: {},
    columnFilters: {}
  },
  emits: ["update:sorting", "update:columnFilters", "form-activity"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

/**
 * Column ids that render through `cellText`'s location-chain formatting below (`barn`, `to_barn`,
 * `from_barn`). A farm/barn/batch string runs long enough that the table's `auto` layout squeezes it
 * against shorter neighbouring columns without a floor - given once here, in the one place every
 * records page's location cell renders, rather than repeated in each page's own column definitions.
 */
const LOCATION_COLUMN_IDS: ReadonlySet<string> = new Set(['barn', 'to_barn', 'from_barn']);
function withLocationColumnWidth(column: ColumnDef<TRow>): ColumnDef<TRow> {
    if (column.id === undefined || !LOCATION_COLUMN_IDS.has(column.id)) {
        return column;
    }

    return {
        ...column,
        meta: {
            ...column.meta,
            headerClass: [column.meta?.headerClass, LOCATION_COLUMN_MIN_WIDTH].filter(Boolean).join(' '),
            cellClass: [column.meta?.cellClass, LOCATION_COLUMN_MIN_WIDTH].filter(Boolean).join(' '),
        },
    };
}

/**
 * Pins the actions column to the right edge of the table's own horizontal scroll area - see
 * `FacetedDataTable`'s `meta.sticky` handling. Every records page declares this column identically
 * (`{ id: RECORD_ACTIONS_COLUMN_ID, header: '', meta: { align: 'right' } }`), so the pin lives here
 * once rather than in each page's column definitions.
 */
function withActionsColumnSticky(column: ColumnDef<TRow>): ColumnDef<TRow> {
    if (column.id !== RECORD_ACTIONS_COLUMN_ID) {
        return column;
    }

    return {
        ...column,
        meta: {
            ...column.meta,
            sticky: 'right',
        },
    };
}

/** `props.columns`, widened with a location-column floor and a pinned actions column - see
 * `withLocationColumnWidth` / `withActionsColumnSticky`. Every read of the page's columns below goes
 * through this rather than the prop directly, so the width and pin hold whichever slot, facet or
 * cell ends up rendering that column. */
const tableColumns = computed(() => props.columns.map(withLocationColumnWidth).map(withActionsColumnSticky));

/**
 * The header facets, read off the columns that declare themselves filterable.
 *
 * Taking the title from the column's own heading is the point: a facet named separately drifts from
 * the column it filters the first time one of the two is reworded.
 */
const facets = computed(() =>
    Object.fromEntries(
        tableColumns.value
            .filter((column) => column.id !== undefined && column.meta?.filterable === true)
            .map((column) => [column.id, { title: String(column.header ?? ''), options: props.facetOptions[column.id as string] ?? [] }])
    )
);

/** Every column rendered as text, paired with the slot name the data table looks for. */
const cellColumns = computed(() =>
    tableColumns.value.filter((column) => column.id !== undefined && column.id !== RECORD_ACTIONS_COLUMN_ID).map((column) => ({ id: column.id as string, slot: `item[${column.id}]` as const }))
);

/**
 * Below this, a records table's Farm / Barn cell no longer has room for both parts - see
 * `locationLabel`'s `compact` option. The records table sits inside the dashboard's fixed sidebar
 * layout, so viewport width tracks the table's own width closely enough that a global media query
 * is simpler than measuring the table's container.
 */
const isNarrow = useMediaQuery('(max-width: 767px)');

/**
 * What one cell reads.
 *
 * The date, the counts, the weights and both location chains render the same way whichever
 * registration type the page shows, so they live here rather than being restated per page.
 */
function cellText(columnId: string, row: TRow): string {
    switch (columnId) {
        case 'date':
            return i18n.global.d(parseISO(row.date), 'short');
        // Named for the level they resolve to - the barn - not for the wire's own `from_location`
        // and `to_location`, which are the registration's own ends and can sit deeper, at a pen or a
        // batch. Those deeper ends are on the wire but not shown.
        case 'from_barn':
            return locationLabel(row.from_farm ?? null, row.from_barn ?? null, { compact: isNarrow.value });
        case 'to_barn':
            return locationLabel(row.to_farm ?? null, row.to_barn ?? null, { compact: isNarrow.value });
        case 'from_section':
            return row.from_section?.name ?? '';
        case 'to_section':
            return row.to_section?.name ?? '';
        case 'barn':
            return locationLabel(row.farm ?? null, row.barn ?? null, { compact: isNarrow.value });
        case 'section':
            return row.section?.name ?? '';
        case 'invoice':
            return row.invoice ?? '';
        case 'customer':
            return row.customer ?? '';
        case 'feed_category':
            return ((row as Record<string, unknown>).feed_mix_category_name as string | null | undefined) ?? NO_VALUE;
        // A deleted/anonymized creator arrives as null.
        case 'created_by':
            return row.created_by ?? NO_VALUE;
        // `decimal` rather than `numeric` for a whole count: it is the app's grouped format, and
        // a four-figure count is unreadable without the separator the weights beside it already get.
        case 'number':
            return i18n.global.n(row.number, 'decimal');
        case 'total_weight':
            return row.total_weight === null ? NO_VALUE : i18n.global.n(row.total_weight, 'decimal');
        case 'avg_weight':
            return row.avg_weight === null ? NO_VALUE : i18n.global.n(row.avg_weight, 'decimal');
        // Slaughters' own weight/discarded/meat-percentage figures - same grouped-decimal treatment
        // as `total_weight` / `avg_weight` above, just under their own column ids since a slaughter
        // carries two independent weight pairs rather than the one every other records page has.
        // The meat percentage is a plain number here, not a `percent`-styled one: the wire already
        // sends it as `61.1`, and its column heading carries the unit.
        case 'total_live_weight':
        case 'avg_live_weight':
        case 'total_slaughter_weight':
        case 'avg_slaughter_weight':
        case 'avg_meat_percent':
        case 'discarded_pigs':
        case 'avg_discarded_weight':
        case 'payment_weight': {
            const value = (row as Record<string, unknown>)[columnId] as number | null;

            return value === null ? NO_VALUE : i18n.global.n(value, 'decimal');
        }
        default:
            // Any column beyond the shared figures and location chains above is a plain string field
            // a page carries on its own row type - Deaths' cause/category are the first of these.
            // The shared table only guarantees `RecordTableRow`'s own fields, so this reads it back
            // untyped rather than widening that type for every page-specific field.
            return String((row as Record<string, unknown>)[columnId] ?? '');
    }
}

function rowIdOf(row: TRow): string {
    return String(row.record_id);
}

/** The slot name a totals-subheader cell for the given column id renders under. */
function totalsSubheaderSlot(columnId: string): `totals[${string}]` {
    return `totals[${columnId}]`;
}

const { stepConfig, initialState, header, loadingRecordId, openRecordForm } = useRecordFormModal();

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", null, [
        _createElementVNode("h2", _hoisted_2, _toDisplayString(_ctx.heading), 1),
        _createElementVNode("p", _hoisted_3, _toDisplayString(_ctx.subtitle), 1)
      ]),
      _renderSlot(_ctx.$slots, "actions")
    ]),
    _createVNode(ActionModal, {
      "initial-step-index": 1,
      "initial-state": _unref(initialState),
      "step-config": _unref(stepConfig),
      header: _unref(header),
      onClosing: _cache[2] || (_cache[2] = ($event: any) => (emit('form-activity')))
    }, {
      default: _withCtx(({ open }) => [
        _createVNode(_unref(FacetedDataTable), {
          data: _ctx.rows,
          columns: tableColumns.value,
          facets: facets.value,
          loading: _ctx.loading,
          "enable-pagination": false,
          page: 1,
          "page-size": Math.max(_ctx.rows.length, 1),
          "page-size-options": [],
          "max-body-height": MAX_BODY_HEIGHT,
          sorting: _ctx.sorting,
          "column-filters": _ctx.columnFilters,
          "row-id": rowIdOf,
          "onUpdate:sorting": _cache[0] || (_cache[0] = ($event: any) => (emit('update:sorting', $event))),
          "onUpdate:columnFilters": _cache[1] || (_cache[1] = ($event: any) => (emit('update:columnFilters', $event)))
        }, _createSlots({
          "item[_actions]": _withCtx(({ item }) => [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_unref(Button), {
                variant: "ghost",
                size: "icon",
                class: "rounded-full -mr-1",
                disabled: _unref(loadingRecordId) !== null,
                onClick: ($event: any) => (_unref(openRecordForm)(item.record_id, open))
              }, {
                default: _withCtx(() => [
                  (_unref(loadingRecordId) === item.record_id)
                    ? (_openBlock(), _createBlock(Spinner, {
                        key: 0,
                        class: "h-5 w-5",
                        "aria-hidden": "true"
                      }))
                    : (_openBlock(), _createBlock(_unref(ChevronRight), {
                        key: 1,
                        class: "h-5 w-5",
                        "aria-hidden": "true"
                      }))
                ]),
                _: 2
              }, 1032, ["disabled", "onClick"])
            ])
          ]),
          _: 2
        }, [
          _renderList(cellColumns.value, (column) => {
            return {
              name: column.slot,
              fn: _withCtx(({ item }) => [
                _createTextVNode(_toDisplayString(cellText(column.id, item)), 1)
              ])
            }
          }),
          (_ctx.totals)
            ? {
                name: "totals[date]",
                fn: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('views.pigs.records.table.totals.total')), 1)
                ]),
                key: "0"
              }
            : undefined,
          (_ctx.totals)
            ? {
                name: "totals[number]",
                fn: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$n(_ctx.totals!.totalPigs, 'decimal')), 1)
                ]),
                key: "1"
              }
            : undefined,
          (_ctx.totals)
            ? {
                name: "totals[total_weight]",
                fn: _withCtx(() => [
                  _createElementVNode("span", {
                    title: _ctx.totals!.hasRowsWithoutWeight ? _ctx.$t('views.pigs.records.table.totals.weightedAverageHint') : undefined
                  }, [
                    _createTextVNode(_toDisplayString(_ctx.$n(_ctx.totals!.totalWeight, 'decimal')) + " ", 1),
                    (_ctx.totals!.hasRowsWithoutWeight)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                          _createTextVNode(_toDisplayString(INCOMPLETE_MARKER))
                        ], 64))
                      : _createCommentVNode("", true)
                  ], 8, _hoisted_5)
                ]),
                key: "2"
              }
            : undefined,
          (_ctx.totals)
            ? {
                name: "totals[avg_weight]",
                fn: _withCtx(() => [
                  _createElementVNode("span", {
                    title: _ctx.totals!.hasRowsWithoutWeight ? _ctx.$t('views.pigs.records.table.totals.weightedAverageHint') : undefined
                  }, [
                    _createTextVNode(_toDisplayString(_ctx.totals!.weightedAverageWeight === null ? NO_VALUE : _ctx.$n(_ctx.totals!.weightedAverageWeight, 'decimal')) + " ", 1),
                    (_ctx.totals!.hasRowsWithoutWeight)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                          _createTextVNode(_toDisplayString(INCOMPLETE_MARKER))
                        ], 64))
                      : _createCommentVNode("", true)
                  ], 8, _hoisted_6)
                ]),
                key: "3"
              }
            : undefined,
          _renderList(_ctx.totalsSubheader, (text, columnId) => {
            return {
              name: totalsSubheaderSlot(columnId),
              fn: _withCtx(() => [
                _createTextVNode(_toDisplayString(text), 1)
              ])
            }
          })
        ]), 1032, ["data", "columns", "facets", "loading", "page-size", "sorting", "column-filters"])
      ]),
      _: 1
    }, 8, ["initial-state", "step-config", "header"])
  ]))
}
}

})